import Vue from 'vue';
import VueRouter from 'vue-router';
import HomeView from '../views/HomeView.vue';
import LoginView from '../views/LoginView.vue';
import RegisterView from '../views/RegisterView.vue';
import ForgotPasswordView from '../views/ForgotPasswordView.vue';
import DashboardView from '../views/DashboardView.vue';
import PolicyView from '../views/PolicyView.vue';
import GettingStartedView from '../views/GettingStartedView.vue';
import OverviewView from '../views/dashboard/OverviewView.vue';
import ApiKeysView from '../views/dashboard/ApiKeysView.vue';
import ModelsView from '../views/dashboard/ModelsView.vue';
import UsageView from '../views/dashboard/UsageView.vue';
import QuotaRecordsView from '../views/dashboard/QuotaRecordsView.vue';
import SubscriptionView from '../views/dashboard/SubscriptionView.vue';
import RedeemView from '../views/dashboard/RedeemView.vue';
import ProfileView from '../views/dashboard/ProfileView.vue';
import BillingView from '../views/dashboard/BillingView.vue';
import AnnouncementsView from '../views/dashboard/AnnouncementsView.vue';
import FeedbackView from '../views/dashboard/FeedbackView.vue';
import ProxyGuideView from '../views/dashboard/ProxyGuideView.vue';
import ChatPage from '../views/ChatPage.vue';
import { fetchProfile } from '../api/profile';

Vue.use(VueRouter);

const LOGIN_STORAGE_KEY = 'codex-api-login-user';
const TOKEN_STORAGE_KEY = 'codex-api-auth-token';

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: { title: '首页' }
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { title: '登录' }
  },
  {
    path: '/register',
    name: 'register',
    component: RegisterView,
    meta: { title: '注册' }
  },
  {
    path: '/forgot-password',
    name: 'forgotPassword',
    component: ForgotPasswordView,
    meta: { title: '找回密码' }
  },
  {
    path: '/dashboard',
    component: DashboardView,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboardOverview',
        component: OverviewView,
        meta: { title: '仪表盘', subtitle: '欢迎回来，这是您的账户概览。' }
      },
      {
        path: 'keys',
        name: 'dashboardKeys',
        component: ApiKeysView,
        meta: { title: 'API 密钥', subtitle: '创建、查看和管理您的访问密钥。' }
      },
      {
        path: 'models',
        name: 'dashboardModels',
        component: ModelsView,
        meta: { title: '可用模型', subtitle: '查看当前公开支持的模型及 Token 计费价格。' }
      },
      {
        path: 'usage',
        name: 'dashboardUsage',
        component: UsageView,
        meta: { title: '使用记录', subtitle: '查看请求明细、Token 消耗和调用状态。' }
      },
      {
        path: 'quota-records',
        name: 'dashboardQuotaRecords',
        component: QuotaRecordsView,
        meta: { title: '额度记录', subtitle: '' }
      },
      {
        path: 'checkin',
        name: 'dashboardCheckin',
        component: () => import('../views/dashboard/CheckinView.vue'),
        meta: { title: '每日赠额度', subtitle: '平台每日赠送使用额度，领取后即可使用。' }
      },
      {
        path: 'subscription',
        name: 'dashboardSubscription',
        component: SubscriptionView,
        meta: { title: '我的套餐', subtitle: '查看当前套餐、用量进度和升级方案。' }
      },
      {
        path: 'redeem',
        name: 'dashboardRedeem',
        component: RedeemView,
        meta: { title: '兑换', browserTitle: '兑换码使用', subtitle: '使用兑换码领取余额或日卡套餐。' }
      },
      {
        path: 'profile',
        name: 'dashboardProfile',
        component: ProfileView,
        meta: { title: '个人资料', subtitle: '维护账号资料和通知偏好。' }
      },
      {
        path: 'billing',
        name: 'dashboardBilling',
        component: BillingView,
        meta: { title: '充值订阅', browserTitle: '充值', subtitle: '' }
      },
      {
        path: 'feedback',
        name: 'dashboardFeedback',
        component: FeedbackView,
        meta: { title: '我的工单', subtitle: '提交建议或问题，查看处理状态和管理员回复。' }
      },
      {
        path: 'announcements',
        name: 'dashboardAnnouncements',
        component: AnnouncementsView,
        meta: { title: '公告', subtitle: '查看系统公告和阅读状态' }
      },
      {
        path: 'proxy-guide',
        name: 'dashboardProxyGuide',
        component: ProxyGuideView,
        meta: {
          title: '我要开通代理',
          subtitle: '了解如何快速拥有自己的代理服务站点。',
          requiresProxy: true
        }
      }
    ]
  },
  {
    path: '/terms',
    name: 'terms',
    component: PolicyView,
    meta: { title: '服务条款' }
  },
  {
    path: '/guide',
    name: 'guide',
    component: GettingStartedView,
    meta: { title: '新手指南' }
  },
  {
    path: '/dashboard/chat',
    name: 'chat',
    component: ChatPage,
    meta: { requiresAuth: true, title: '测试模型' }
  }
];

const router = new VueRouter({
  mode: 'history',
  routes
});

router.afterEach(to => {
  const title = to.meta.browserTitle || to.meta.title;
  document.title = title ? `${title}-中转站` : '中转站';
});

router.beforeEach(async (to, from, next) => {
  const loggedIn = Boolean(
    window.localStorage.getItem(LOGIN_STORAGE_KEY) &&
    window.localStorage.getItem(TOKEN_STORAGE_KEY)
  );

  if (to.matched.some(record => record.meta.requiresAuth) && !loggedIn) {
    window.location.href = '/login?redirect=' + encodeURIComponent(to.fullPath);
    next(false);
    return;
  }

  if (to.matched.some(record => record.meta.requiresProxy)) {
    try {
      const { data } = await fetchProfile();
      const allowed = data && data.code === 0 && data.data && Number(data.data.allowProxy) === 1;
      if (!allowed) {
        next({ name: 'dashboardOverview' });
        return;
      }
    } catch (error) {
      next({ name: 'dashboardOverview' });
      return;
    }
  }

  next();
});

export default router;
